import React, { lazy, Suspense } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SocketProvider } from './contexts/SocketContext';
import { ThemeProvider } from './contexts/ThemeContext';
 
// Pages
const Home = lazy(() => import('./pages/Home'));
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Assets = lazy(() => import('./pages/Assets'));
const Payments = lazy(() => import('./pages/Payments'));
const Crypto = lazy(() => import('./pages/Crypto'));
const Convert = lazy(() => import('./pages/Convert'));
const Documents = lazy(() => import('./pages/Documents'));
const Products = lazy(() => import('./pages/Products'));
const Setting = lazy(() => import('./pages/Setting'));
const Profile = lazy(() => import('./pages/Profile'));
const Loans = lazy(() => import('./pages/Loans'));
const LoanDetails = lazy(() => import('./pages/LoanDetails'));
const LoanApplication = lazy(() => import('./pages/LoanApplication'));
const CreditLine = lazy(() => import('./pages/Creditline'));
 
// Admin pages
const AdminPanel = lazy(() => import('./pages/admin/AdminPanel'));
const AdminUserView = lazy(() => import('./pages/admin/AdminUserView'));
const AdminLoans = lazy(() => import('./pages/admin/AdminLoans'));
const AdminChat = lazy(() => import('./pages/admin/AdminChat'));
const AdminWallets = lazy(() => import('./pages/admin/AdminWallets'));
const OwnerPanel = lazy(() => import('./pages/admin/OwnerPanel'));
 
// Static pages
const About = lazy(() => import('./pages/static/About'));
const Contacts = lazy(() => import('./pages/static/Contacts'));
const Legal = lazy(() => import('./pages/static/Legal'));
const CookiePolicy = lazy(() => import('./pages/static/CookiePolicy'));
const PrivacyPolicy = lazy(() => import('./pages/static/PrivacyPolicy'));
const Services = lazy(() => import('./pages/static/Services'));
const Cards = lazy(() => import('./pages/static/Cards'));
const Security = lazy(() => import('./pages/static/Security'));
const FAQ = lazy(() => import('./pages/static/FAQ'));
const Business = lazy(() => import('./pages/static/Business'));
const Savings = lazy(() => import('./pages/static/Savings'));
const Investments = lazy(() => import('./pages/static/Investments'));
const Transfers = lazy(() => import('./pages/static/Transfers'));
const CryptoServices = lazy(() => import('./pages/static/CryptoServices'));
const Tariffs = lazy(() => import('./pages/static/Tariffs'));
 
// Components
const ChatWidget = lazy(() => import('./components/ChatWidget'));
 
const ADMIN_ROLES = ['admin', 'super_admin', 'owner'];
 
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return children;
};
 
const AdminRoute = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!user || !ADMIN_ROLES.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return children;
};
 
const OwnerRoute = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!user || user.role !== 'owner') return <Navigate to="/dashboard" replace />;
  return children;
};
 
function AppRoutes() {
  return (
    <Suspense fallback={<div />}>
      <Routes>
        {/* Public */}
        <Route path="/" element={<Home />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Register />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/about" element={<About />} />
        <Route path="/contacts" element={<Contacts />} />
        <Route path="/legal" element={<Legal />} />
        <Route path="/cookie-policy" element={<CookiePolicy />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/services" element={<Services />} />
        <Route path="/cards" element={<Cards />} />
        <Route path="/security" element={<Security />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/business" element={<Business />} />
        {/* Individual service pages */}
        <Route path="/savings" element={<Savings />} />
        <Route path="/investments" element={<Investments />} />
        <Route path="/transfers" element={<Transfers />} />
        <Route path="/crypto-services" element={<CryptoServices />} />
        <Route path="/tariffs" element={<Tariffs />} />
        {/* Aliases for services */}
        <Route path="/accounts" element={<Services />} />
        <Route path="/mobile" element={<Services />} />
 
        {/* Protected — User */}
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/assets" element={<ProtectedRoute><Assets /></ProtectedRoute>} />
        <Route path="/transactions" element={<ProtectedRoute><Assets /></ProtectedRoute>} />
        <Route path="/payments" element={<ProtectedRoute><Payments /></ProtectedRoute>} />
        <Route path="/crypto" element={<ProtectedRoute><Crypto /></ProtectedRoute>} />
        <Route path="/convert" element={<ProtectedRoute><Convert /></ProtectedRoute>} />
        <Route path="/documents" element={<ProtectedRoute><Documents /></ProtectedRoute>} />
        <Route path="/products" element={<ProtectedRoute><Products /></ProtectedRoute>} />
        <Route path="/setting" element={<ProtectedRoute><Setting /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
        <Route path="/loans" element={<ProtectedRoute><Loans /></ProtectedRoute>} />
        <Route path="/loans/apply" element={<ProtectedRoute><LoanApplication /></ProtectedRoute>} />
        <Route path="/loans/credit-line" element={<ProtectedRoute><CreditLine /></ProtectedRoute>} />
        <Route path="/loans/:id" element={<ProtectedRoute><LoanDetails /></ProtectedRoute>} />
 
        {/* Card route — renders Payments with card tab active */}
        <Route path="/card" element={<ProtectedRoute><Payments /></ProtectedRoute>} />
 
        {/* Protected — Admin */}
        <Route path="/admin" element={<AdminRoute><AdminPanel /></AdminRoute>} />
        <Route path="/admin/users/:id" element={<AdminRoute><AdminUserView /></AdminRoute>} />
        <Route path="/admin/loans" element={<AdminRoute><AdminLoans /></AdminRoute>} />
        <Route path="/admin/chat" element={<AdminRoute><AdminChat /></AdminRoute>} />
        <Route path="/admin/wallets" element={<AdminRoute><AdminWallets /></AdminRoute>} />
 
        {/* Protected — Owner */}
        <Route path="/admin/owner" element={<OwnerRoute><OwnerPanel /></OwnerRoute>} />
 
        {/* Legacy routes */}
        <Route path="/bnpl" element={<Home />} />
        <Route path="/top-up" element={<Home />} />
        <Route path="/cashback" element={<Home />} />
        <Route path="/security" element={<Home />} />
 
        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
 
      {/* Global Chat Widget */}
      <ChatWidget />
    </Suspense>
  );
}
 
function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <AuthProvider>
          <SocketProvider>
            <BrowserRouter>
              <AppRoutes />
            </BrowserRouter>
          </SocketProvider>
        </AuthProvider>
      </ThemeProvider>
    </div>
  );
}
 
export default App;